import React, { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './layouts/AppLayout';
import { Home } from './pages/Home';
import { PublicLanding } from './pages/PublicLanding';
import { PublicForm } from './pages/PublicForm';
import { ThankYou } from './pages/ThankYou';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { Dashboard } from './pages/app/Dashboard';
import { Leads } from './pages/app/Leads';
import { LeadDetail } from './pages/app/LeadDetail';
import { Pipeline } from './pages/app/Pipeline';
import { Trash } from './pages/app/Trash';
import { Forms } from './pages/app/Forms';
import { FormEditor } from './pages/app/FormEditor';
import { Landings } from './pages/app/Landings';
import { LandingEditor } from './pages/app/LandingEditor';
import { Team } from './pages/app/Team';
import { Settings } from './pages/app/Settings';
import { Integrations } from './pages/app/Integrations';

/** Protección de la zona privada con la sesión real de Supabase. */
function RequireAuth({
  children
}: {
  children: ReactNode;
}) {
  const {
    session,
    restoring
  } = useAuth();
  const location = useLocation();
  if (restoring) {
    return <div className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-0" data-mp="2zlv6ktmtuzuj">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1" data-mp="33nq6btp41567" />
        <span className="sr-only" data-id="element-2" data-mp="38kqce4lym7xb">Cargando tu espacio de trabajo…</span>
      </div>;
  }
  if (!session) {
    return <Navigate to="/login" state={{
      from: location.pathname
    }} replace data-id="element-3" data-mp="3dlns8ajhypvn" />;
  }
  return <>{children}</>;
}
export function App() {
  return <BrowserRouter data-id="element-4" data-mp="suncc5gqgafj">
      <AuthProvider data-id="element-5" data-mp="su44xiopl4fr">
        <Routes data-id="element-6" data-mp="stkns46vuk2s">
          {/* Zona pública */}
          <Route path="/" element={<Home data-id="element-8" data-mp="35yq6nee9s31" />} data-id="element-7" data-mp="swgmvvugvest" />
          <Route path="/landing/:slug" element={<PublicLanding data-id="element-10" data-mp="38kwkjpju1jo" />} data-id="element-9" data-mp="35gld2do25ji" />
          <Route path="/formulario/:slug" element={<PublicForm data-id="element-12" data-mp="3ubzumd3utnfp" />} data-id="element-11" data-mp="3ud1g7bihxsbo" />
          <Route path="/gracias/:slug" element={<ThankYou data-id="element-14" data-mp="d57g95652mk4" />} data-id="element-13" data-mp="3uevsk1fpcc45" />
          <Route path="/login" element={<Login data-id="element-16" data-mp="d3wa4yp812pu" />} data-id="element-15" data-mp="d3mil6oj2ni1" />
          <Route path="/registro" element={<Register data-id="element-18" data-mp="85bd1859mz5m" />} data-id="element-17" data-mp="85l2mumef4as" />
          <Route path="/recuperar-contrasena" element={<ForgotPassword data-id="element-20" data-mp="3gle9siuub3f6" />} data-id="element-19" data-mp="864atjm8yebm" />

          {/* Zona privada */}
          <Route path="/app" element={<RequireAuth data-id="element-22" data-mp="3bmbdpnwu87jc">
                <AppLayout data-id="element-23" data-mp="3bn2y02kziiub" />
              </RequireAuth>} data-id="element-21" data-mp="3glo067jsrcle">
            <Route index element={<Navigate to="/app/dashboard" replace data-id="element-25" data-mp="3qb7lrkyw9kjy" />} data-id="element-24" data-mp="3bk72ekwm0691" />
            <Route path="dashboard" element={<Dashboard data-id="element-27" data-mp="3qd0owqaatmn7" />} data-id="element-26" data-mp="3qcqzb3t61hi1" />
            <Route path="leads" element={<Leads data-id="element-29" data-mp="3ljpkswwvfjyz" />} data-id="element-28" data-mp="3ljxyus4qcvbr" />
            <Route path="leads/:id" element={<LeadDetail data-id="element-31" data-mp="2xun3xra29v5j" />} data-id="element-30" data-mp="3liefutrow66a" />
            <Route path="pipeline" element={<Pipeline data-id="element-33" data-mp="2xssga57enoxr" />} data-id="element-32" data-mp="2xtv94ioag1gb" />
            <Route path="papelera" element={<Trash data-id="element-35" data-mp="2suqydnlptvnv" />} data-id="element-34" data-mp="2svtr812lm86f" />
            <Route path="formularios" element={<Forms data-id="element-37" data-mp="18dri7slud9c8" />} data-id="element-36" data-mp="2svaf50zmqe0b" />
            <Route path="formularios/nuevo" element={<Forms data-id="element-39" data-mp="18c6lbwc21eb7" />} data-id="element-38" data-mp="18d9gwhkbo4xg" />
            <Route path="formularios/:id" element={<FormEditor data-id="element-41" data-mp="1d4rdktfbbjcm" />} data-id="element-40" data-mp="18by8g4fxm900" />
            <Route path="landings" element={<Landings data-id="element-43" data-mp="1d6aywyftskl9" />} data-id="element-42" data-mp="1d62gkr0xon2o" />
            <Route path="landings/nueva" element={<Landings data-id="element-45" data-mp="yh6erbph06ai" />} data-id="element-44" data-mp="yg3l4w0rjpqw" />
            <Route path="landings/:id/editor" element={<LandingEditor data-id="element-47" data-mp="13dwufxxd4zve" />} data-id="element-46" data-mp="ydqulugxw728" />
            <Route path="equipo" element={<Team data-id="element-49" data-mp="1r498zrn45uuy" />} data-id="element-48" data-mp="13ey3xxyqdeb0" />
            <Route path="configuracion" element={<Settings data-id="element-51" data-mp="1r4sgeef510uq" />} data-id="element-50" data-mp="1r3r5qb21agdj" />
            <Route path="integraciones" element={<Integrations data-id="element-53" data-mp="1w3vj3s7nauzj" />} data-id="element-52" data-mp="1r2g005p12yjs" />
          </Route>

          <Route path="*" element={<Navigate to="/" replace data-id="element-55" data-mp="1i3ksmqa8ziew" />} data-id="element-54" data-mp="1w2kg3um0xr9h" />
        </Routes>
      </AuthProvider>
    </BrowserRouter>;
}